import * as React from 'react';
import { useCallback, useState } from 'react';
import {
  Button,
  makeStyles,
  MessageBar,
  MessageBarBody,
  Tab,
  TabList,
  Text,
  tokens,
} from '@fluentui/react-components';
import { Sparkle16Regular } from '@fluentui/react-icons';
import LoadingIcon from './LoadingIcon';
import RiskCard, { RiskStatus } from './RiskCard';
import { DocumentAnalysisResult, RiskType } from '../../services/analysis';
import { analyzeDocumentViaService } from '../../services/wordRiskAnalysis';
import { isAccountPlanDisabledError } from '../../services/planLimit';
import { selectByText } from '../../word/selection';

interface AnalysisTabProps {
  /** Returns true if the change was applied (false if user cancelled). */
  onApply: (rewrite: string) => Promise<boolean> | void;
  /** A plan/quota-limit error was hit — parent collapses to the upgrade gate. */
  onPlanLimitHit: () => void;
}

type FilterValue = 'all' | RiskType;

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  intro: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  runRow: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  loadingRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
  },
  summaryBox: {
    padding: '10px 12px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
  },
  summaryLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
    marginBottom: '6px',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
  },
  summaryText: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
  },
  empty: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
  scrollMiss: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
    marginTop: '-4px',
  },
});

const AnalysisTab: React.FC<AnalysisTabProps> = ({ onApply, onPlanLimitHit }) => {
  const styles = useStyles();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<DocumentAnalysisResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterValue>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [scrollMiss, setScrollMiss] = useState(false);
  const [statusByRisk, setStatusByRisk] = useState<Record<string, RiskStatus>>({});

  const runAnalysis = useCallback(async () => {
    setError(null);
    setLoading(true);
    setResult(null);
    setExpandedId(null);
    setScrollMiss(false);
    setStatusByRisk({});
    try {
      const r = await analyzeDocumentViaService();
      setResult(r);
    } catch (e) {
      if (isAccountPlanDisabledError(e)) {
        onPlanLimitHit();
      } else {
        setError(e instanceof Error ? e.message : 'Analysis failed.');
      }
    } finally {
      setLoading(false);
    }
  }, [onPlanLimitHit]);

  const handleToggle = useCallback(
    async (riskId: string, snippet: string) => {
      if (statusByRisk[riskId] && statusByRisk[riskId] !== 'active') return;
      const willExpand = expandedId !== riskId;
      setExpandedId(willExpand ? riskId : null);
      setScrollMiss(false);
      if (willExpand) {
        try {
          const found = await selectByText(snippet);
          if (!found) setScrollMiss(true);
        } catch {
          setScrollMiss(true);
        }
      }
    },
    [expandedId, statusByRisk],
  );

  const handleApplyRisk = useCallback(
    async (riskId: string, rewrite: string) => {
      const result = onApply(rewrite);
      const applied = result instanceof Promise ? await result : true;
      if (applied) {
        setStatusByRisk(prev => ({ ...prev, [riskId]: 'applied' }));
        if (expandedId === riskId) setExpandedId(null);
      }
    },
    [onApply, expandedId],
  );

  const handleDismissRisk = useCallback(
    (riskId: string) => {
      setStatusByRisk(prev => ({ ...prev, [riskId]: 'dismissed' }));
      if (expandedId === riskId) setExpandedId(null);
      setScrollMiss(false);
    },
    [expandedId],
  );

  const counts = React.useMemo(() => {
    if (!result) return { all: 0, legal: 0, business: 0, structural: 0 };
    return {
      all: result.risks.length,
      legal: result.risks.filter(r => r.type === 'legal').length,
      business: result.risks.filter(r => r.type === 'business').length,
      structural: result.risks.filter(r => r.type === 'structural').length,
    };
  }, [result]);

  const visibleRisks = React.useMemo(() => {
    if (!result) return [];
    if (filter === 'all') return result.risks;
    return result.risks.filter(r => r.type === filter);
  }, [result, filter]);

  return (
    <div className={styles.root}>
      <Text className={styles.intro}>
        Run a risk analysis to surface legal, business, and structural risks. Click a risk to jump
        to it in the document.
      </Text>

      <div className={styles.runRow}>
        <Button appearance="primary" onClick={runAnalysis} disabled={loading}>
          {result ? 'Re-analyze risks' : 'Analyze risks'}
        </Button>
        {loading && (
          <span className={styles.loadingRow}>
            <LoadingIcon size={24} /> <span>Analyzing…</span>
          </span>
        )}
      </div>

      {error && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      {result && (
        <>
          <div className={styles.summaryBox}>
            <div className={styles.summaryLabel}>
              <Sparkle16Regular /> AI summary
            </div>
            <div className={styles.summaryText}>{result.summary}</div>
          </div>

          <TabList
            size="small"
            selectedValue={filter}
            onTabSelect={(_, data) => {
              setFilter(data.value as FilterValue);
              setExpandedId(null);
              setScrollMiss(false);
            }}
          >
            <Tab value="all">All {counts.all}</Tab>
            <Tab value="legal">Legal {counts.legal}</Tab>
            <Tab value="business">Business {counts.business}</Tab>
            <Tab value="structural">Structural {counts.structural}</Tab>
          </TabList>

          {scrollMiss && (
            <Text className={styles.scrollMiss}>
              Couldn’t locate this clause in the document — text may have been edited.
            </Text>
          )}

          {visibleRisks.length === 0 && (
            <Text className={styles.empty}>
              {result.risks.length === 0
                ? 'No risks identified. Looks clean.'
                : 'No risks of this type.'}
            </Text>
          )}

          {visibleRisks.map(risk => (
            <RiskCard
              key={risk.id}
              risk={risk}
              status={statusByRisk[risk.id] ?? 'active'}
              expanded={expandedId === risk.id}
              onToggle={() => handleToggle(risk.id, risk.snippet)}
              onApply={rewrite => handleApplyRisk(risk.id, rewrite)}
              onDismiss={() => handleDismissRisk(risk.id)}
            />
          ))}
        </>
      )}
    </div>
  );
};

export default AnalysisTab;
